<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :title="$t('model.customReport')"
    resource="custom_reports"
    :onFetchData="onFetchData"
  />
</template>

<script>
import { Vue, Component, Prop, Mixins } from 'vue-property-decorator';
import { filterColumnsExtend, columnsExtend, formColumnsExtend } from '../admin_grapes/custom_reports.js';
import _ from 'lodash';
import moment from 'moment';
import Utils from '../components/utils.vue';

const TARGET_TYPES = {
  Application: '平台',
  Customer: '客户',
  User: '业务员',
  CustomerSource: '客户来源',
  CustomerLevel: '客户级别',
}

export const PERCENT_CELL = function (h, { props: { value } }) {
  if (_.isNil(value)) { return }

  return <span>{(value * 100).toFixed(2)}%</span>
}

const NEW_CUSTOMER_COUNT_COLUMNS = [{
  prop: 'data.new_customer_count',
  label: '新增客户数',
  width: 140,
  sortable: true,
}, {
  prop: 'data.new_customer_count_change_rate_by_last',
  label: '新增客户数环比',
  renderCell: PERCENT_CELL
}, {
  prop: 'data.new_customer_count_change_rate_by_last_year',
  label: '新增客户数同比',
  renderCell: PERCENT_CELL
}]

const NEW_PAID_CUSTOMER_COUNT_COLUMNS = [{
  prop: 'data.new_paid_customer_count',
  label: '新增付费客户数',
  width: 140,
  sortable: true,
}, {
  prop: 'data.new_paid_customer_count_change_rate_by_last',
  label: '新增付费客户数环比',
  renderCell: PERCENT_CELL
}, {
  prop: 'data.new_paid_customer_count_change_rate_by_last_year',
  label: '新增付费客户数同比',
  renderCell: PERCENT_CELL
}]

const PAID_RATE_COLUMNS = [{
  prop: 'data.paid_rate',
  label: '转化率',
  width: 120,
  sortable: true,
  renderCell: PERCENT_CELL
}, {
  prop: 'data.paid_rate_change_rate_by_last',
  label: '转化率环比',
  renderCell: PERCENT_CELL
}, {
  prop: 'data.paid_rate_change_rate_by_last_year',
  label: '转化率同比',
  renderCell: PERCENT_CELL
}]

const ORDER_AMOUNT_COLUMNS = [{
  prop: 'data.order_amount',
  label: '订单金额',
  width: 140,
  sortable: true,
}, {
  prop: 'data.order_amount_change_rate_by_last',
  label: '订单金额环比',
  renderCell: PERCENT_CELL
}, {
  prop: 'data.order_amount_change_rate_by_last_year',
  label: '订单金额同比',
  renderCell: PERCENT_CELL
}]

const CUSTOMER_COUNT_COLUMNS = [{
  prop: 'data.customer_count',
  label: '客户数',
  width: 120,
  sortable: true,
}, {
  prop: 'data.customer_count_change_rate_by_last',
  label: '客户数环比',
  renderCell: PERCENT_CELL
}, {
  prop: 'data.customer_count_change_rate_by_last_year',
  label: '客户数同比',
  renderCell: PERCENT_CELL
}]

const COLUMNS_OF_TARGET_TYPE = {
  Application: [
    ...NEW_CUSTOMER_COUNT_COLUMNS,
    ...NEW_PAID_CUSTOMER_COUNT_COLUMNS,
    ...PAID_RATE_COLUMNS,
    ...ORDER_AMOUNT_COLUMNS,
  ],
  CustomerLevel: [
    ...CUSTOMER_COUNT_COLUMNS,
    ...ORDER_AMOUNT_COLUMNS,
  ],
  CustomerSource: [
    ...CUSTOMER_COUNT_COLUMNS,
    ...ORDER_AMOUNT_COLUMNS,
  ],
  User: [
    ...ORDER_AMOUNT_COLUMNS,
  ],
  Customer: [
    ...ORDER_AMOUNT_COLUMNS,
  ]
}

@Component
export default class CustomReports extends Mixins(Vue, Utils) {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}
  targetType

  async onFetchData({ type }) {
    if (type === 'index') {
      const { data, pagination } = await this.$request.get('/custom_reports', { params: { ...this.$route.query } });
      this.targetType = _.get(this, '$route.query.target_type_eq')
      if (!this.target_type) {
        const targetTypes = _.uniq(_.map(data, 'target_type'))
        if (targetTypes.length === 1) {
          this.targetType = targetTypes[0]
        }
      }
      return {
        data,
        pagination
      }
    }

    return {}
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  filterColumns() {
    const array = [
      {
        prop: 'target_type_eq',
        label: this.$t('customReport.targetType'),
        renderCell: {
          component: 'select',
          props: {
            options: _.map(TARGET_TYPES, (k, v) => ({ label: k, value: v }))
          }
        }
      },
      {
        key: 'kind_eq',
        renderCell: {
          props: {
            clearable: false,
          }
        }
      }
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  columns(h, { type }) {
    const array = [
      type === 'show' && 'id',
      {
        key: 'target_type',
        renderCell(h, { props: { value } }) {
          return <span>{TARGET_TYPES[value]}</span>
        }
      },
      'target',
      this.roleKind === 'application' && 'owner',
      'kind',
      {
        key: 'start_at',
        sortable: true,
        renderCell(h, { props: { scope: { row } } }) {
          if (row.kind === 'monthly') {
            return <span>{moment(row.start_at).format('YYYY-MM')}</span>
          } else {
            return <span>{moment(row.start_at).format('YYYY-MM-DD')}</span>
          }
        }
      },
      {
        key: 'end_at',
        sortable: true,
        renderCell(h, { props: { scope: { row } } }) {
          if (row.kind === 'monthly') {
            return <span>{moment(row.end_at).format('YYYY-MM')}</span>
          } else {
            return <span>{moment(row.end_at).format('YYYY-MM-DD')}</span>
          }
        }
      },
      !this.targetType && {
        key: 'data',
        renderCell(h, { props: { scope: { row } } }) {
          return <span>{JSON.stringify(row.data)}</span>
        }
      },
    ].filter(Boolean)
    if (COLUMNS_OF_TARGET_TYPE[this.targetType]) {
      array.push(...COLUMNS_OF_TARGET_TYPE[this.targetType])
    }

    return columnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource - RESTful api resoure name
   * @param {Object} params.data - current form data
   * @param {Function} params.change - invoke this method to sync form change({ field: 'new value' })
   */
  formColumns(h, { data, type }) {
    const array = [

    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove }) {
    return [
      {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'custom_reports.edit', params: { id: row.id } }
        }
      },
      {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'custom_reports.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }
}

export const route = [
  {
    path: '/custom_reports',
    name: 'custom_reports.index',
    component: CustomReports,
    props: {
      type: 'index',
    }
  },
  {
    path: '/custom_reports/new',
    name: 'custom_reports.new',
    component: CustomReports,
    props: {
      type: 'new',
    }
  },
  {
    path: '/custom_reports/:id',
    name: 'custom_reports.show',
    component: CustomReports,
    props: {
      type: 'show',
    }
  },
  {
    path: '/custom_reports/:id/edit',
    name: 'custom_reports.edit',
    component: CustomReports,
    props: {
      type: 'edit'
    }
  }
]
</script>
